import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi, apiClient } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Building2, Plus, Search, Filter, Edit, Trash2, Eye, ExternalLink,
  MapPin, Phone, Mail, Users, ChevronLeft, ChevronRight, X, 
  CheckCircle, AlertCircle, AlertTriangle, XCircle, Award, Loader2, CheckCircle2,
  ChevronUp, ChevronDown, ChevronsUpDown, FileText, History, MessageSquare, Calendar
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { TIPOS_ENTIDADE, CARGOS_CONTACTO, PRIORIDADES_ATENDIMENTO, ORIGENS_INSTITUICAO } from '../constants/cargos';

const SITUACOES_FINANCEIRAS = ['Deficitária', 'Equilibrada', 'Excedentária'];

// Estado da Instituição conforme 4º Steering ENTRAJUDA (25/set/2026)
const ESTADOS_INSTITUICAO = [
  { value: 'Em análise', label: 'Em análise', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'Ativa', label: 'Ativa', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'Suspensa', label: 'Suspensa', color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'Inativa', label: 'Inativa', color: 'bg-red-100 text-red-800 border-red-300' }
];

const getEstadoConfig = (estado) => {
  const config = ESTADOS_INSTITUICAO.find(e => e.value === estado);
  return config || { value: estado, label: estado || 'Ativa', color: 'bg-slate-100 text-slate-800 border-slate-300' };
};

// Badge de qualidade da informação
const QualidadeBadge = ({ instituicaoId }) => {
  const [qualidade, setQualidade] = useState(null);
  
  useEffect(() => {
    const fetchQualidade = async () => {
      try {
        const response = await apiClient.get(`/instituicoes/${instituicaoId}/qualidade-informacao`);
        setQualidade(response.data);
      } catch (error) {
        console.error('Erro ao carregar qualidade:', error);
      }
    };
    fetchQualidade();
  }, [instituicaoId]);
  
  if (!qualidade) return null;
  
  const badgeConfig = {
    excelente: { icon: CheckCircle, class: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', label: 'Excelente' },
    bom: { icon: CheckCircle, class: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', label: 'Bom' },
    atencao: { icon: AlertTriangle, class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Atenção' },
    desatualizado: { icon: XCircle, class: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', label: 'Desatualizado' }
  };
  
  const config = badgeConfig[qualidade.badge] || badgeConfig.atencao;
  const Icon = config.icon;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className={`${config.class} cursor-help`}>
            <Icon className="w-3 h-3 mr-1" />
            {qualidade.percentagem_preenchimento}%
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium">{config.label}</p>
          <p className="text-xs mt-1">
            {qualidade.campos_preenchidos}/{qualidade.total_campos} campos preenchidos
          </p>
          {!qualidade.campanha_respondida && (
            <p className="text-xs text-amber-500 mt-1">
              Campanha {qualidade.campanha_ano} não respondida
            </p>
          )}
          {qualidade.campos_em_falta?.length > 0 && (
            <p className="text-xs text-red-500 mt-1">
              Em falta: {qualidade.campos_em_falta.slice(0, 3).map(c => c.label).join(', ')}
              {qualidade.campos_em_falta.length > 3 && ` +${qualidade.campos_em_falta.length - 3}`}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Componente para mostrar utilizadores do portal associados a uma instituição
const UtilizadoresPortalSection = ({ instituicaoId }) => {
  const [utilizadores, setUtilizadores] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  
  useEffect(() => {
    if (instituicaoId && expanded) {
      loadUtilizadores();
    }
  }, [instituicaoId, expanded]);
  
  const loadUtilizadores = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/instituicoes/${instituicaoId}/utilizadores-portal`);
      setUtilizadores(response.data);
    } catch (error) {
      console.error('Erro ao carregar utilizadores:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <p className="text-sm font-semibold text-purple-700 dark:text-purple-300 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Utilizadores do Portal
        </p>
        <span className="text-purple-500 text-xs">
          {expanded ? '▲ Fechar' : '▼ Expandir'}
        </span>
      </button>
      
      {expanded && (
        <div className="mt-3">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
              <span className="ml-2 text-sm text-slate-500">A carregar...</span>
            </div>
          ) : utilizadores?.utilizadores?.length > 0 ? (
            <div className="space-y-2">
              {utilizadores.utilizadores.map((u, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded border">
                  <div>
                    <p className="font-medium text-sm">{u.nome || u.email}</p>
                    <p className="text-xs text-slate-500">{u.email}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={u.ativo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {u.ativo ? 'Ativo' : 'Inativo'}
                    </Badge>
                    <p className="text-xs text-slate-400 mt-1">{u.role}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-2">
              Nenhum utilizador do portal associado a esta instituição.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Combobox de pesquisa de valências — mostra só o nome, oculta código CRM
function ValenciaCombobox({ value, onChange, valencias }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return valencias;
    const s = search.toLowerCase();
    return valencias.filter(v => v.nome.toLowerCase().includes(s) || v.categoria?.toLowerCase().includes(s));
  }, [valencias, search]);

  const selected = valencias.find(v => v.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal text-left"
        >
          <span className="truncate">{selected ? selected.nome : 'Selecione a resposta social...'}</span>
          <ChevronsUpDown className="w-4 h-4 ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-2" align="start">
        <div className="flex items-center border rounded-md px-3 mb-2 bg-white dark:bg-slate-800">
          <Search className="w-4 h-4 mr-2 text-slate-400 shrink-0" />
          <input
            autoFocus
            className="flex-1 py-1.5 text-sm bg-transparent outline-none placeholder:text-slate-400"
            placeholder="Pesquisar por nome ou categoria..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {filtered.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">Nenhuma resposta social encontrada</p>
          ) : (
            filtered.map(v => (
              <button
                key={v.id}
                type="button"
                onClick={() => { onChange(v.id); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-between ${value === v.id ? 'bg-[#0066CC]/10 text-[#0066CC] dark:text-blue-400' : 'text-slate-800 dark:text-slate-200'}`}
              >
                <span>{v.nome}</span>
                <span className="text-[10px] text-slate-400 ml-2 shrink-0">{v.categoria}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function InstituicoesPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [instituicoes, setInstituicoes] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [valencias, setValencias] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDistrito, setSelectedDistrito] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedTipo, setSelectedTipo] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'nome', direction: 'asc' });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedInstituicao, setSelectedInstituicao] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dados'); // Tab ativa na view modal
  
  // Documentos em Aberto (Conta Corrente)
  const [documentosPendentes, setDocumentosPendentes] = useState([]);
  const [documentosHistorico, setDocumentosHistorico] = useState([]);
  const [novoDocumento, setNovoDocumento] = useState({ tipo: '', descricao: '', data_limite: '' });
  
  // Histórico de Alterações
  const [historico, setHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  
  // Registo de Contactos
  const [contactosRegistados, setContactosRegistados] = useState([]);
  const [novoContacto, setNovoContacto] = useState({ tipo: '', assunto: '', descricao: '', data: '' });
  const [loadingContactos, setLoadingContactos] = useState(false);
  
  // Demographics modal for valencia
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [demoValenciaIndex, setDemoValenciaIndex] = useState(null);
  const [demoFaixas, setDemoFaixas] = useState([]);
  const [demoFaixasForm, setDemoFaixasForm] = useState({});
  const [loadingDemoFaixas, setLoadingDemoFaixas] = useState(false);

  // Estado para demografia de valências nas moradas adicionais
  const [moradaDemoModalOpen, setMoradaDemoModalOpen] = useState(false);
  const [moradaDemoValenciaIdx, setMoradaDemoValenciaIdx] = useState(null);
  const [moradaDemoFaixas, setMoradaDemoFaixas] = useState([]);
  const [moradaDemoFaixasForm, setMoradaDemoFaixasForm] = useState({});
  const [loadingMoradaDemoFaixas, setLoadingMoradaDemoFaixas] = useState(false);
  
  // Form
  const [formData, setFormData] = useState({
    codigo_ea: '',
    nome: '',
    tipo_entidade: '',
    distrito_id: '',
    concelho_id: '',
    freguesia: '',
    morada: '',
    codigo_postal: '',
    numero_porta: '',
    telefone: '',
    email_principal: '',
    website: '',
    pessoa_contacto: '',
    nif: '',
    representante_legal: '',
    num_tecnicos: 0,
    num_outros_colaboradores: 0,
    num_voluntarios: 0,
    situacao_financeira: 'Equilibrada',
    apoio_banco_alimentar: false,
    codigo_banco_alimentar: '',
    // Presidente / Direção
    presidente_nome: '',
    presidente_cargo: '',
    presidente_telefone: '',
    presidente_email: '',
    // Pessoa de Contacto Institucional
    contacto_nome: '',
    contacto_cargo: '',
    contacto_telefone: '',
    contacto_email: '',
    // Origem / Classificação
    origem: '',
    referenciado_por: '',
    prioridade_atendimento: '',
    observacoes_prioridade: '',
    // Redes Sociais
    redes_sociais: { facebook: '', instagram: '', linkedin: '' },
    valencias: [],
    // Múltiplas moradas adicionais
    moradas_adicionais: [],
    // Múltiplos contactos adicionais
    contactos_adicionais: []
  });
  const [formLoading, setFormLoading] = useState(false);
  const [concelhos, setConcelhos] = useState([]);
  const [codigoPostalLoading, setCodigoPostalLoading] = useState(false);
  const [codigoPostalSuggestion, setCodigoPostalSuggestion] = useState(null);
  const [showMoradasAdicionais, setShowMoradasAdicionais] = useState(false);
  const [showContactosAdicionais, setShowContactosAdicionais] = useState(false);

  // Lookup código postal com integração distritos/concelhos (geoapi.pt + fallback local)
  const lookupCodigoPostal = async (codigo) => {
    if (!codigo || codigo.replace(/[^0-9]/g, '').length < 4) return;
    
    try {
      setCodigoPostalLoading(true);
      
      // Usar smart-lookup que tenta geoapi.pt primeiro e depois fallback local
      const response = await apiClient.get(`/codigos-postais/smart-lookup?codigo=${encodeURIComponent(codigo)}`);
      const data = response.data;
      
      if (data && data.found) {
        setCodigoPostalSuggestion(data);
        
        // Carregar concelhos se houver distrito_id
        if (data.distrito_id) {
          try {
            const concelhosResp = await apiClient.get(`/concelhos?distrito_id=${data.distrito_id}`);
            setConcelhos(concelhosResp.data || []);
          } catch (err) {
            console.error('Error loading concelhos:', err);
          }
        }
        
        // Carregar freguesias se houver concelho_id
        if (data.concelho_id) {
          try {
            const _fResp = await apiClient.get(`/freguesias?concelho_id=${data.concelho_id}`);
            // setFreguesias is managed by the geo form context — ignore here
            console.debug('Freguesias loaded:', _fResp.data?.length);
          } catch (err) {
            console.error('Error loading freguesias:', err);
          }
        }
        
        // Auto-preencher campos
        setFormData(prev => ({
          ...prev,
          morada: data.rua && !prev.morada ? data.rua : prev.morada,
          localidade: data.localidade || prev.localidade,
          freguesia: data.freguesia || data.localidade || prev.freguesia,
          distrito_id: data.distrito_id || prev.distrito_id,
          concelho_id: data.concelho_id || prev.concelho_id
        }));
        
        // Feedback
        const feedbackParts = [];
        if (data.rua) feedbackParts.push(data.rua);
        if (data.freguesia && data.freguesia !== data.localidade) feedbackParts.push(data.freguesia);
        if (data.localidade) feedbackParts.push(data.localidade);
        if (data.concelho) feedbackParts.push(data.concelho);
        if (data.distrito) feedbackParts.push(data.distrito);
        
        const sourceInfo = data.source === 'geoapi' ? ' (CTT)' : '';
        toast.success(`Localização${sourceInfo}: ${feedbackParts.join(', ')}`);
      } else {
        setCodigoPostalSuggestion(null);
        toast.info(data?.message || 'Código postal não encontrado. Por favor preencha manualmente.');
      }
    } catch (error) {
      console.error('Error looking up codigo postal:', error);
      setCodigoPostalSuggestion(null);
    } finally {
      setCodigoPostalLoading(false);
    }
  };

  // Debounce código postal lookup
  const handleCodigoPostalChange = (value) => {
    setFormData(prev => ({ ...prev, codigo_postal: value }));
    // Lookup quando tiver pelo menos 4 dígitos
    const cleanValue = value.replace(/[^0-9]/g, '');
    if (cleanValue.length >= 4) {
      // Debounce - wait 500ms after typing stops
      clearTimeout(window.codigoPostalTimeout);
      window.codigoPostalTimeout = setTimeout(() => {
        lookupCodigoPostal(value);
      }, 500);
    }
  };

  // Funções para múltiplas moradas
  const [moradaDialogOpen, setMoradaDialogOpen] = useState(false);
  const [moradaEditIdx, setMoradaEditIdx] = useState(null); // null = nova morada
  const [moradaForm, setMoradaForm] = useState({
    designacao: '', morada: '', codigo_postal: '', localidade: '',
    concelho: '', distrito: '', telefone: '', email: '', valencias: [], observacoes: ''
  });
  const [moradaCpLoading, setMoradaCpLoading] = useState(false);

  const openMoradaDialog = (idx = null) => {
    if (idx !== null && formData.moradas_adicionais[idx]) {
      const m = formData.moradas_adicionais[idx];
      setMoradaForm({
        designacao: m.designacao || m.tipo || '',
        morada: m.morada || '',
        codigo_postal: m.codigo_postal || '',
        localidade: m.localidade || '',
        concelho: m.concelho || '',
        distrito: m.distrito || '',
        telefone: m.telefone || '',
        email: m.email || '',
        valencias: m.valencias || [],
        observacoes: m.observacoes || ''
      });
    } else {
      setMoradaForm({ designacao: '', morada: '', codigo_postal: '', localidade: '',
        concelho: '', distrito: '', telefone: '', email: '', valencias: [], observacoes: '' });
    }
    setMoradaEditIdx(idx);
    setMoradaDialogOpen(true);
  };

  const handleMoradaCPLookup = async (cp) => {
    const clean = cp.trim();
    if (clean.length < 4) return;
    setMoradaCpLoading(true);
    try {
      const resp = await apiClient.get(`/codigos-postais/smart-lookup?codigo=${encodeURIComponent(clean)}`);
      const data = resp.data;
      if (data?.found) {
        setMoradaForm(f => ({
          ...f,
          codigo_postal: clean,
          localidade: data.localidade || f.localidade,
          concelho: data.concelho || f.concelho,
          distrito: data.distrito || f.distrito
        }));
        toast.success(`Localização: ${[data.localidade, data.concelho].filter(Boolean).join(', ')}`);
      } else {
        toast.info('CP não encontrado — preencha manualmente.');
      }
    } catch (_) {
      toast.info('Não foi possível obter dados do CP.');
    } finally {
      setMoradaCpLoading(false);
    }
  };

  const saveMoradaDialog = () => {
    if (!moradaForm.morada.trim()) { toast.error('A morada é obrigatória'); return; }
    const moradaData = { ...moradaForm, id: moradaEditIdx !== null ? (formData.moradas_adicionais[moradaEditIdx]?.id || Date.now()) : Date.now() };
    setFormData(prev => {
      const updated = [...prev.moradas_adicionais];
      if (moradaEditIdx !== null) {
        updated[moradaEditIdx] = moradaData;
      } else {
        updated.push(moradaData);
      }
      return { ...prev, moradas_adicionais: updated };
    });
    setMoradaDialogOpen(false);
  };

  const addMoradaAdicional = () => openMoradaDialog(null);

  // Funções para múltiplos contactos
  const addContactoAdicional = () => {
    setFormData(prev => ({
      ...prev,
      contactos_adicionais: [
        ...prev.contactos_adicionais,
        { id: Date.now(), nome: '', cargo: '', email: '', telefone: '' }
      ]
    }));
    setShowContactosAdicionais(true);
  };

  const updateContactoAdicional = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      contactos_adicionais: prev.contactos_adicionais.map((c, i) => 
        i === index ? { ...c, [field]: value } : c
      )
    }));
  };

  const removeContactoAdicional = (index) => {
    setFormData(prev => ({
      ...prev,
      contactos_adicionais: prev.contactos_adicionais.filter((_, i) => i !== index)
    }));
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [instData, distData, valData] = await Promise.all([
        api.getInstituicoes(),
        api.getDistritos(),
        api.getValencias()
      ]);
      setInstituicoes(instData);
      setDistritos(distData);
      setValencias(valData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Filtered data
  const filteredInstituicoes = useMemo(() => {
    return instituicoes.filter(inst => {
      const matchesSearch = !searchTerm || 
        (inst.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (inst.codigo_ea || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDistrito = !selectedDistrito || inst.distrito_id === selectedDistrito;
      const matchesStatus = !selectedStatus || inst.estado === selectedStatus;
      const matchesTipo = !selectedTipo || inst.tipo_entidade === selectedTipo;
      return matchesSearch && matchesDistrito && matchesStatus && matchesTipo;
    });
  }, [instituicoes, searchTerm, selectedDistrito, selectedStatus, selectedTipo]);

  // Sort filtered data
  const sortedInstituicoes = useMemo(() => {
    if (!sortConfig.key) return filteredInstituicoes;
    
    return [...filteredInstituicoes].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle special cases
      if (sortConfig.key === 'total_utentes') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      
      // Handle null/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;
      
      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle strings
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredInstituicoes, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedInstituicoes.length / itemsPerPage);
  const paginatedData = sortedInstituicoes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetForm = () => {
    setFormData({
      codigo_ea: '',
      nome: '',
      tipo_entidade: '',
      distrito_id: '',
      concelho_id: '',
      freguesia: '',
      morada: '',
      codigo_postal: '',
      numero_porta: '',
      telefone: '',
      email_principal: '',
      website: '',
      pessoa_contacto: '',
      nif: '',
      representante_legal: '',
      num_tecnicos: 0,
      num_outros_colaboradores: 0,
      num_voluntarios: 0,
      situacao_financeira: 'Equilibrada',
      apoio_banco_alimentar: false,
      codigo_banco_alimentar: '',
      presidente_nome: '',
      presidente_cargo: '',
      presidente_telefone: '',
      presidente_email: '',
      contacto_nome: '',
      contacto_cargo: '',
      contacto_telefone: '',
      contacto_email: '',
      origem: '',
      referenciado_por: '',
      prioridade_atendimento: '',
      observacoes_prioridade: '',
      redes_sociais: { facebook: '', instagram: '', linkedin: '' },
      valencias: [],
      moradas_adicionais: [],
      contactos_adicionais: []
    });
    setConcelhos([]);
    setShowMoradasAdicionais(false);
    setShowContactosAdicionais(false);
  };

  const openCreateModal = () => {
    resetForm();
    setModalMode('create');
    setModalOpen(true);
  };

  const openEditModal = async (inst) => {
    setFormData({
      codigo_ea: inst.codigo_ea,
      nome: inst.nome,
      tipo_entidade: inst.tipo_entidade,
      estado: inst.estado || 'Ativa',
      distrito_id: inst.distrito_id,
      concelho_id: inst.concelho_id || '',
      freguesia: inst.freguesia || '',
      morada: inst.morada || '',
      codigo_postal: inst.codigo_postal || '',
      numero_porta: inst.numero_porta || '',
      telefone: inst.telefone || '',
      email_principal: inst.email_principal || '',
      website: inst.website || '',
      pessoa_contacto: inst.pessoa_contacto || '',
      nif: inst.nif || '',
      representante_legal: inst.representante_legal || '',
      num_tecnicos: inst.num_tecnicos || 0,
      num_outros_colaboradores: inst.num_outros_colaboradores || 0,
      num_voluntarios: inst.num_voluntarios || 0,
      situacao_financeira: inst.situacao_financeira || 'Equilibrada',
      apoio_banco_alimentar: inst.apoio_banco_alimentar || false,
      codigo_banco_alimentar: inst.codigo_banco_alimentar || '',
      // Presidente / Direção
      presidente_nome: inst.presidente_nome || '',
      presidente_cargo: inst.presidente_cargo || '',
      presidente_telefone: inst.presidente_telefone || '',
      presidente_email: inst.presidente_email || '',
      // Pessoa de Contacto Institucional — alinha contacto_nome com pessoa_contacto (legado)
      contacto_nome: inst.contacto_nome || inst.pessoa_contacto || '',
      contacto_cargo: inst.contacto_cargo || '',
      contacto_telefone: inst.contacto_telefone || '',
      contacto_email: inst.contacto_email || '',
      // Origem / Classificação
      origem: inst.origem || '',
      referenciado_por: inst.referenciado_por || '',
      prioridade_atendimento: inst.prioridade_atendimento || '',
      observacoes_prioridade: inst.observacoes_prioridade || '',
      // Redes Sociais
      redes_sociais: inst.redes_sociais || { facebook: '', instagram: '', linkedin: '' },
      valencias: inst.valencias?.map(v => ({
        valencia_id: v.valencia_id,
        numero_utentes: v.numero_utentes || 0,
        capacidade_maxima: v.capacidade_maxima || 0,
        utentes_masculino: v.utentes_masculino || 0,
        utentes_feminino: v.utentes_feminino || 0,
        ativa: v.ativa !== false
      })) || [],
      moradas_adicionais: inst.moradas_adicionais || [],
      contactos_adicionais: inst.contactos_adicionais || []
    });
    setShowMoradasAdicionais(inst.moradas_adicionais?.length > 0);
    setShowContactosAdicionais(inst.contactos_adicionais?.length > 0);
    // Load concelhos for the distrito
    if (inst.distrito_id) {
      try {
        const response = await apiClient.get(`/concelhos?distrito_id=${inst.distrito_id}`);
        setConcelhos(response.data);
      } catch (error) {
        console.error('Error loading concelhos:', error);
      }
    }
    // Carregar moradas da coleção dedicada (fonte única de verdade)
    if (inst.id) {
      try {
        const moradasResp = await apiClient.get(`/instituicoes/${inst.id}/moradas`);
        const moradasDb = moradasResp.data || [];
        setFormData(prev => ({
          ...prev,
          moradas_adicionais: moradasDb
        }));
        setShowMoradasAdicionais(moradasDb.length > 0);
      } catch (_) {
        // fallback: manter moradas embutidas
      }
    }
    setSelectedInstituicao(inst);
    setModalMode('edit');
    setModalOpen(true);
  };

  const openViewModal = async (inst) => {
    setSelectedInstituicao(inst);
    setActiveTab('dados');
    setViewModalOpen(true);
    
    // Carregar dados adicionais da instituição
    await loadDocumentosPendentes(inst.id);
    await loadHistorico(inst.id);
    await loadContactosRegistados(inst.id);
  };

  // Abrir modal de edição automaticamente se vier com parâmetro edit
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && instituicoes.length > 0) {
      const instToEdit = instituicoes.find(i => i.id === editId);
      if (instToEdit) {
        openEditModal(instToEdit);
        // Limpar o parâmetro da URL
        setSearchParams({});
      }
    }
  }, [searchParams, instituicoes]);

  // ============================================
  // DOCUMENTOS EM ABERTO (CONTA CORRENTE)
  // ============================================
  const loadDocumentosPendentes = async (instituicaoId) => {
    try {
      const response = await apiClient.get(`/instituicoes/${instituicaoId}/documentos`);
      setDocumentosPendentes(response.data.pendentes || []);
      setDocumentosHistorico(response.data.historico || []);
    } catch (error) {
      console.error('Error loading documentos:', error);
      setDocumentosPendentes([]);
      setDocumentosHistorico([]);
    }
  };

  const addDocumentoPendente = async () => {
    if (!novoDocumento.tipo || !novoDocumento.descricao) {
      toast.error('Tipo e descrição são obrigatórios');
      return;
    }
    try {
      await apiClient.post(`/instituicoes/${selectedInstituicao.id}/documentos`, novoDocumento);
      toast.success('Documento adicionado com sucesso');
      setNovoDocumento({ tipo: '', descricao: '', data_limite: '' });
      await loadDocumentosPendentes(selectedInstituicao.id);
    } catch (error) {
      toast.error('Erro ao adicionar documento');
    }
  };

  const marcarDocumentoEntregue = async (docId) => {
    try {
      await apiClient.put(`/instituicoes/${selectedInstituicao.id}/documentos/${docId}/entregue`);
      toast.success('Documento marcado como entregue');
      await loadDocumentosPendentes(selectedInstituicao.id);
    } catch (error) {
      toast.error('Erro ao atualizar documento');
    }
  };

  // ============================================
  // HISTÓRICO DE ALTERAÇÕES
  // ============================================
  const loadHistorico = async (instituicaoId) => {
    setLoadingHistorico(true);
    try {
      const response = await apiClient.get(`/instituicoes/${instituicaoId}/historico`);
      setHistorico(response.data || []);
    } catch (error) {
      console.error('Error loading historico:', error);
      setHistorico([]);
    } finally {
      setLoadingHistorico(false);
    }
  };

  // ============================================
  // REGISTO DE CONTACTOS COM INSTITUIÇÃO
  // ============================================
  const loadContactosRegistados = async (instituicaoId) => {
    setLoadingContactos(true);
    try {
      const response = await apiClient.get(`/instituicoes/${instituicaoId}/contactos`);
      setContactosRegistados(response.data || []);
    } catch (error) {
      console.error('Error loading contactos:', error);
      setContactosRegistados([]);
    } finally {
      setLoadingContactos(false);
    }
  };

  const addContactoRegistado = async () => {
    if (!novoContacto.tipo || !novoContacto.assunto) {
      toast.error('Tipo e assunto são obrigatórios');
      return;
    }
    try {
      await apiClient.post(`/instituicoes/${selectedInstituicao.id}/contactos`, {
        ...novoContacto,
        data: novoContacto.data || new Date().toISOString().split('T')[0]
      });
      toast.success('Contacto registado com sucesso');
      setNovoContacto({ tipo: '', assunto: '', descricao: '', data: '' });
      await loadContactosRegistados(selectedInstituicao.id);
    } catch (error) {
      toast.error('Erro ao registar contacto');
    }
  };

  const deleteContactoRegistado = async (contactoId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este registo?')) return;
    try {
      await apiClient.delete(`/instituicoes/${selectedInstituicao.id}/contactos/${contactoId}`);
      toast.success('Registo eliminado');
      await loadContactosRegistados(selectedInstituicao.id);
    } catch (error) {
      toast.error('Erro ao eliminar registo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate valencias
    for (const val of formData.valencias) {
      if (!val.valencia_id) continue;
      
      const valenciaInfo = valencias.find(v => v.id === val.valencia_id);
      const totalDemo = (val.utentes_masculino || 0) + (val.utentes_feminino || 0);
      
      // Check demographic data - utentes > 0 requires demographic data
      if (val.numero_utentes > 0 && totalDemo === 0) {
        toast.error(`A resposta social "${valenciaInfo?.nome}" tem ${val.numero_utentes} utentes mas falta dados demográficos. Clique em "Definir" para preencher.`);
        return;
      }
      
      // Demographic total should match numero_utentes
      if (totalDemo > 0 && totalDemo !== val.numero_utentes) {
        // Auto-correct: use demographic total as the source of truth
        val.numero_utentes = totalDemo;
      }
      
      // Check over-capacity (only if capacity is defined)
      if (val.capacidade_maxima > 0 && val.numero_utentes > val.capacidade_maxima) {
        const continuar = window.confirm(
          `A resposta social "${valenciaInfo?.nome}" tem sobre-utilização (${val.numero_utentes} utentes para capacidade de ${val.capacidade_maxima}). Deseja continuar?`
        );
        if (!continuar) return;
      }
    }
    
    setFormLoading(true);
    
    try {
      if (modalMode === 'create') {
        await api.createInstituicao(formData);
        toast.success('Instituição criada com sucesso!');
      } else {
        await api.updateInstituicao(selectedInstituicao.id, formData);
        toast.success('Instituição atualizada com sucesso!');
      }
      setModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(error.response?.data?.detail || 'Erro ao guardar instituição');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDistritoChange = async (distritoId) => {
    setFormData(prev => ({ ...prev, distrito_id: distritoId, concelho_id: '' }));
    if (distritoId) {
      try {
        const response = await apiClient.get(`/concelhos?distrito_id=${distritoId}`);
        setConcelhos(response.data);
      } catch (error) {
        console.error('Error loading concelhos:', error);
        setConcelhos([]);
      }
    } else {
      setConcelhos([]);
    }
  };

  const handleDelete = async (inst) => {
    if (!window.confirm(`Tem a certeza que pretende desativar "${inst.nome}"?`)) return;
    
    try {
      await api.deleteInstituicao(inst.id);
      toast.success('Instituição desativada com sucesso');
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erro ao desativar instituição');
    }
  };

  const addValencia = () => {
    setFormData(prev => ({
      ...prev,
      valencias: [...prev.valencias, { 
        valencia_id: '', 
        numero_utentes: 0, 
        capacidade_maxima: 0,
        utentes_masculino: 0,
        utentes_feminino: 0,
        ativa: true
      }]
    }));
  };

  const updateValencia = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      valencias: prev.valencias.map((v, i) => 
        i === index ? { ...v, [field]: field === 'valencia_id' ? value : parseInt(value) || 0 } : v
      )
    }));
  };

  const removeValencia = (index) => {
    setFormData(prev => ({
      ...prev,
      valencias: prev.valencias.filter((_, i) => i !== index)
    }));
  };
  
  // Open demographics modal for a valencia
  const openDemoModal = async (index) => {
    const valencia = formData.valencias[index];
    if (!valencia.valencia_id) {
      toast.error('Selecione primeiro uma resposta social');
      return;
    }
    
    setDemoValenciaIndex(index);
    setLoadingDemoFaixas(true);
    
    try {
      const response = await apiClient.get(`/demografia/valencias/${valencia.valencia_id}/faixas-etarias`);
      const faixas = response.data?.faixas_etarias || [];
      setDemoFaixas(faixas);
      
      // Initialize form with existing data or zeros
      const formInit = {};
      faixas.forEach(f => {
        formInit[`${f.codigo}_masculino`] = valencia[`${f.codigo}_masculino`] || 0;
        formInit[`${f.codigo}_feminino`] = valencia[`${f.codigo}_feminino`] || 0;
      });
      setDemoFaixasForm(formInit);
      setDemoModalOpen(true);
    } catch (error) {
      console.error('Error loading faixas:', error);
      toast.error('Erro ao carregar faixas etárias');
    } finally {
      setLoadingDemoFaixas(false);
    }
  };
  
  // Save demographics from modal
  const saveDemoData = () => {
    if (demoValenciaIndex === null) return;
    
    // Calculate totals
    let masculino = 0;
    let feminino = 0;
    Object.entries(demoFaixasForm).forEach(([key, value]) => {
      if (key.endsWith('_masculino')) masculino += value || 0;
      if (key.endsWith('_feminino')) feminino += value || 0;
    });
    
    const totalUtentes = masculino + feminino;
    
    // Update valencia with all faixa data plus totals AND numero_utentes
    setFormData(prev => {
      const updatedValencias = prev.valencias.map((v, i) => {
        if (i === demoValenciaIndex) {
          const capacidade = v.capacidade_maxima || 0;
          // Validate capacity
          if (capacidade > 0 && totalUtentes > capacidade) {
            toast.warning(`Atenção: ${totalUtentes} utentes excede capacidade de ${capacidade}`);
          }
          return { 
            ...v, 
            ...demoFaixasForm,
            utentes_masculino: masculino,
            utentes_feminino: feminino,
            numero_utentes: totalUtentes // Auto-update total
          };
        }
        return v;
      });
      return { ...prev, valencias: updatedValencias };
    });
    
    setDemoModalOpen(false);
    toast.success('Dados demográficos atualizados');
  };
  
  // Calculate totals from faixas form
  const calcularTotaisDemoFaixas = () => {
    let masculino = 0;
    let feminino = 0;
    Object.entries(demoFaixasForm).forEach(([key, value]) => {
      if (key.endsWith('_masculino')) masculino += value || 0;
      if (key.endsWith('_feminino')) feminino += value || 0;
    });
    return { masculino, feminino, total: masculino + feminino };
  };

  // Abre modal de demografia para valência de uma morada adicional
  const openMoradaDemoModal = async (valenciaIdx) => {
    const valencia = moradaForm.valencias[valenciaIdx];
    if (!valencia?.valencia_id) return;
    setMoradaDemoValenciaIdx(valenciaIdx);
    setLoadingMoradaDemoFaixas(true);
    try {
      const response = await apiClient.get(`/demografia/valencias/${valencia.valencia_id}/faixas-etarias`);
      const faixas = response.data?.faixas_etarias || [];
      setMoradaDemoFaixas(faixas);
      const formInit = {};
      faixas.forEach(f => {
        formInit[`${f.codigo}_masculino`] = valencia[`${f.codigo}_masculino`] || 0;
        formInit[`${f.codigo}_feminino`] = valencia[`${f.codigo}_feminino`] || 0;
      });
      setMoradaDemoFaixasForm(formInit);
      setMoradaDemoModalOpen(true);
    } catch (_) {
      toast.error('Erro ao carregar faixas etárias');
    } finally {
      setLoadingMoradaDemoFaixas(false);
    }
  };

  // Guarda dados demográficos de uma valência de morada
  const saveMoradaDemoData = () => {
    if (moradaDemoValenciaIdx === null) return;
    let masculino = 0, feminino = 0;
    Object.entries(moradaDemoFaixasForm).forEach(([key, value]) => {
      if (key.endsWith('_masculino')) masculino += value || 0;
      if (key.endsWith('_feminino')) feminino += value || 0;
    });
    const total = masculino + feminino;
    setMoradaForm(f => ({
      ...f,
      valencias: f.valencias.map((v, i) => i === moradaDemoValenciaIdx ? {
        ...v, ...moradaDemoFaixasForm,
        utentes_masculino: masculino,
        utentes_feminino: feminino,
        numero_utentes: total
      } : v)
    }));
    setMoradaDemoModalOpen(false);
    toast.success('Dados demográficos da morada actualizados');
  };

  const calcularTotaisMoradaDemoFaixas = () => {
    let masculino = 0, feminino = 0;
    Object.entries(moradaDemoFaixasForm).forEach(([key, value]) => {
      if (key.endsWith('_masculino')) masculino += value || 0;
      if (key.endsWith('_feminino')) feminino += value || 0;
    });
    return { masculino, feminino, total: masculino + feminino };
  };

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  // Get sort icon for column
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronsUpDown className="w-4 h-4 text-slate-400 ml-1" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-[#0066CC] ml-1" />
      : <ChevronDown className="w-4 h-4 text-[#0066CC] ml-1" />;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDistrito('');
    setSelectedStatus('');
    setSelectedTipo('');
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="instituicoes-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="instituicoes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Instituições</h1>
          <p className="text-slate-500 mt-1">Gestão de instituições parceiras da rede ENTRAJUDA</p>
        </div>
        <Button 
          onClick={openCreateModal}
          className="bg-[#0066CC] hover:bg-[#0052a3]"
          data-testid="create-instituicao-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Instituição
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar por nome ou código..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={selectedDistrito || "all"} onValueChange={(v) => { setSelectedDistrito(v === "all" ? "" : v); setCurrentPage(1); }}>
              <SelectTrigger data-testid="filter-distrito">
                <SelectValue placeholder="Distrito" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os distritos</SelectItem>
                {distritos.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedStatus || "all"} onValueChange={(v) => { setSelectedStatus(v === "all" ? "" : v); setCurrentPage(1); }}>
              <SelectTrigger data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Em análise">Em análise</SelectItem>
                <SelectItem value="Ativa">Ativa</SelectItem>
                <SelectItem value="Suspensa">Suspensa</SelectItem>
                <SelectItem value="Inativa">Inativa</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedTipo || "all"} onValueChange={(v) => { setSelectedTipo(v === "all" ? "" : v); setCurrentPage(1); }}>
              <SelectTrigger data-testid="filter-tipo">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                {TIPOS_ENTIDADE.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(searchTerm || selectedDistrito || selectedStatus || selectedTipo) && (
            <div className="flex items-center gap-2 mt-4">
              <span className="text-sm text-slate-500">Filtros ativos:</span>
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[#0066CC]">
                <X className="w-3 h-3 mr-1" />
                Limpar filtros
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {sortedInstituicoes.length} instituições encontradas
        </p>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table data-testid="instituicoes-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Código</TableHead>
                <TableHead 
                  className="font-semibold cursor-pointer select-none hover:bg-slate-100"
                  onClick={() => handleSort('nome')}
                >
                  <div className="flex items-center">
                    Nome
                    {getSortIcon('nome')}
                  </div>
                </TableHead>
                <TableHead 
                  className="font-semibold cursor-pointer select-none hover:bg-slate-100"
                  onClick={() => handleSort('tipo_entidade')}
                >
                  <div className="flex items-center">
                    Tipo
                    {getSortIcon('tipo_entidade')}
                  </div>
                </TableHead>
                <TableHead 
                  className="font-semibold cursor-pointer select-none hover:bg-slate-100"
                  onClick={() => handleSort('distrito_nome')}
                >
                  <div className="flex items-center">
                    Distrito
                    {getSortIcon('distrito_nome')}
                  </div>
                </TableHead>
                <TableHead 
                  className="font-semibold text-center cursor-pointer select-none hover:bg-slate-100"
                  onClick={() => handleSort('total_utentes')}
                >
                  <div className="flex items-center justify-center">
                    Utentes
                    {getSortIcon('total_utentes')}
                  </div>
                </TableHead>
                <TableHead className="font-semibold text-center">Qualidade</TableHead>
                <TableHead 
                  className="font-semibold text-center cursor-pointer select-none hover:bg-slate-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center justify-center">
                    Status
                    {getSortIcon('status')}
                  </div>
                </TableHead>
                <TableHead className="font-semibold text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    Nenhuma instituição encontrada
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((inst) => (
                  <TableRow 
                    key={inst.id} 
                    className="hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                    data-testid={`instituicao-row-${inst.id}`}
                    onClick={() => navigate(`/instituicoes/${inst.id}`)}
                  >
                    <TableCell className="font-mono text-sm">
                      {inst.codigo_ea}
                      {inst.codigo_ea?.startsWith('PROV-') && (
                        <Badge variant="outline" className="ml-2 text-xs text-amber-600">Provisório</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{inst.nome}</p>
                          {inst.certificada_ea && (
                            <Badge className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white text-xs px-1.5 py-0">
                              <Award className="w-3 h-3 mr-0.5" />
                              EA
                            </Badge>
                          )}
                        </div>
                        {inst.pessoa_contacto && (
                          <p className="text-xs text-slate-500">{inst.pessoa_contacto}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{inst.tipo_entidade}</Badge>
                    </TableCell>
                    <TableCell>{inst.distrito_nome}</TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-[#0066CC]">{inst.total_utentes || 0}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <QualidadeBadge instituicaoId={inst.id} />
                    </TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        const estadoConfig = getEstadoConfig(inst.estado);
                        return (
                          <Badge className={`${estadoConfig.color} hover:opacity-80`}>
                            {estadoConfig.label}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/instituicoes/${inst.id}`); }}
                          data-testid={`view-btn-${inst.id}`}
                          title="Ver ficha completa"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); openEditModal(inst); }}
                          data-testid={`edit-btn-${inst.id}`}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => { e.stopPropagation(); handleDelete(inst); }}
                          data-testid={`delete-btn-${inst.id}`}
                          title="Desativar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between py-4 px-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700" data-testid="pagination-controls">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Página {currentPage} de {totalPages} ({sortedInstituicoes.length} resultados)
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="pagination-prev"
              className="flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Anterior</span>
            </Button>
            {/* Page numbers */}
            <div className="hidden sm:flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <Button
                  key={page}
                  variant={page === currentPage ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className={page === currentPage ? "bg-[#0066CC] text-white" : ""}
                  data-testid={`pagination-page-${page}`}
                >
                  {page}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              data-testid="pagination-next"
              className="flex items-center gap-1"
            >
              <span className="hidden sm:inline">Seguinte</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="instituicao-modal">
          <DialogHeader>
            <DialogTitle>
              {modalMode === 'create' ? 'Nova Instituição' : 'Editar Instituição'}
            </DialogTitle>
            <DialogDescription>
              {modalMode === 'create' 
                ? 'Preencha os dados da nova instituição parceira'
                : 'Atualize os dados da instituição'
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Quick Save Button at Top */}
          <div className="flex justify-end pb-2 border-b">
            <Button 
              type="submit"
              form="instituicao-edit-form"
              disabled={formLoading}
              className="bg-[#0066CC] hover:bg-[#0055AA]"
              data-testid="btn-guardar-topo"
            >
              {formLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A guardar...</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Guardar</>
              )}
            </Button>
          </div>
          
          <form id="instituicao-edit-form" onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="codigo_ea">Código EA *</Label>
                <Input
                  id="codigo_ea"
                  value={formData.codigo_ea}
                  onChange={(e) => setFormData(prev => ({ ...prev, codigo_ea: e.target.value }))}
                  placeholder="C000000"
                  required
                  data-testid="input-codigo-ea"
                />
                {modalMode === 'edit' && (
                  <p className="text-xs text-amber-600 mt-1">Atenção: Alterar o código pode afetar referências existentes</p>
                )}
              </div>
              <div>
                <Label htmlFor="tipo_entidade">Tipo de Entidade *</Label>
                <Select 
                  value={formData.tipo_entidade} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, tipo_entidade: v }))}
                >
                  <SelectTrigger data-testid="select-tipo-entidade">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_ENTIDADE.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="estado">Estado da Instituição</Label>
                <Select 
                  value={formData.estado || 'Ativa'} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, estado: v }))}
                >
                  <SelectTrigger data-testid="select-estado">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS_INSTITUICAO.map(estado => (
                      <SelectItem key={estado.value} value={estado.value}>
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${estado.color.includes('green') ? 'bg-green-500' : estado.color.includes('orange') ? 'bg-orange-500' : estado.color.includes('red') ? 'bg-red-500' : 'bg-blue-500'}`}></span>
                          {estado.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {modalMode === 'edit' && (
                  <p className="text-xs text-slate-500 mt-1">Para alterar o estado com registo de motivo, use "Alterar Estado" na ficha da instituição.</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="nome">Nome da Instituição *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Nome completo da instituição"
                required
                data-testid="input-nome"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="distrito_id">Distrito *</Label>
                <Select 
                  value={formData.distrito_id} 
                  onValueChange={handleDistritoChange}
                >
                  <SelectTrigger data-testid="select-distrito">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {distritos.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="concelho_id">Concelho {distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional' ? '(opcional)' : ''}</Label>
                <Select 
                  value={formData.concelho_id} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, concelho_id: v }))}
                  disabled={!formData.distrito_id || distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional'}
                >
                  <SelectTrigger data-testid="select-concelho">
                    <SelectValue placeholder={
                      distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional' 
                        ? "N/A (Internacional)" 
                        : formData.distrito_id 
                          ? "Selecione..." 
                          : "Selecione primeiro o distrito"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {concelhos.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nif">NIF</Label>
                <div className="relative">
                  <Input
                    id="nif"
                    value={formData.nif}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setFormData(prev => ({ ...prev, nif: value }));
                    }}
                    placeholder="123456789"
                    maxLength={9}
                    data-testid="input-nif"
                    className={formData.nif && formData.nif.length === 9 ? 'border-green-500 pr-10' : formData.nif && formData.nif.length > 0 ? 'border-red-500 pr-10' : ''}
                  />
                  {formData.nif && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {formData.nif.length === 9 ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                  )}
                </div>
                {formData.nif && formData.nif.length > 0 && formData.nif.length < 9 && (
                  <p className="text-xs text-red-500 mt-1">NIF deve ter 9 dígitos ({formData.nif.length}/9)</p>
                )}
              </div>
              <div>
                <Label htmlFor="situacao_financeira">Situação Financeira</Label>
                <Select 
                  value={formData.situacao_financeira} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, situacao_financeira: v }))}
                >
                  <SelectTrigger data-testid="select-situacao">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {SITUACOES_FINANCEIRAS.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="morada">Morada</Label>
              <Textarea
                id="morada"
                value={formData.morada}
                onChange={(e) => setFormData(prev => ({ ...prev, morada: e.target.value }))}
                placeholder="Endereço completo"
                rows={2}
                data-testid="input-morada"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="codigo_postal">Código Postal</Label>
                <div className="relative">
                  <Input
                    id="codigo_postal"
                    value={formData.codigo_postal}
                    onChange={(e) => handleCodigoPostalChange(e.target.value)}
                    placeholder="0000-000"
                    data-testid="input-codigo-postal"
                    className={codigoPostalSuggestion?.found ? "border-green-500 pr-8" : ""}
                  />
                  {codigoPostalLoading && (
                    <Loader2 className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
                  )}
                  {codigoPostalSuggestion?.found && !codigoPostalLoading && (
                    <CheckCircle2 className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-green-500" />
                  )}
                </div>
                {codigoPostalSuggestion?.found && (
                  <p className="text-xs text-green-600 mt-1">
                    {codigoPostalSuggestion.localidade}, {codigoPostalSuggestion.concelho}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="numero_porta">Nº Porta</Label>
                <Input
                  id="numero_porta"
                  value={formData.numero_porta || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, numero_porta: e.target.value }))}
                  placeholder="123"
                  data-testid="input-numero-porta"
                />
              </div>
              <div>
                <Label htmlFor="freguesia">Freguesia / Localidade {distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional' ? '(opcional)' : ''}</Label>
                <Input
                  id="freguesia"
                  value={formData.freguesia || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, freguesia: e.target.value }))}
                  placeholder={distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional' ? 'N/A (Internacional)' : 'Preenchido automaticamente ou manual'}
                  data-testid="input-freguesia"
                />
              </div>
            </div>

            {/* Moradas Adicionais */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-slate-700">Moradas Adicionais</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openMoradaDialog(null)}
                  className="text-blue-600 hover:text-blue-700"
                  data-testid="btn-add-morada"
                >
                  <Plus className="w-4 h-4 mr-1" /> Adicionar Morada
                </Button>
              </div>

              {formData.moradas_adicionais?.length > 0 ? (
                <div className="space-y-2">
                  {formData.moradas_adicionais.map((morada, idx) => (
                    <div key={morada.id || idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border">
                      <MapPin className="w-4 h-4 text-[#0066CC] mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {morada.designacao || morada.tipo || 'Morada'}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{morada.morada}</p>
                        {(morada.localidade || morada.codigo_postal) && (
                          <p className="text-xs text-slate-400">{[morada.codigo_postal, morada.localidade, morada.concelho].filter(Boolean).join(' · ')}</p>
                        )}
                        {morada.valencias?.length > 0 && (
                          <p className="text-xs text-blue-600 mt-0.5">{morada.valencias.length} resposta(s) social(ais)</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openMoradaDialog(idx)}
                          className="h-7 w-7 p-0 text-slate-500 hover:text-[#0066CC]"
                          data-testid={`btn-edit-morada-${idx}`}
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, moradas_adicionais: prev.moradas_adicionais.filter((_, i) => i !== idx) }))}
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                          data-testid={`btn-remove-morada-${idx}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">Sem moradas adicionais. Clique em "Adicionar Morada" para adicionar.</p>
              )}
            </div>

            {/* Presidente / Direção */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Presidente / Direção</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="presidente_nome">Nome do Presidente</Label>
                  <Input
                    id="presidente_nome"
                    value={formData.presidente_nome || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, presidente_nome: e.target.value }))}
                    placeholder="Nome completo"
                    data-testid="input-presidente-nome"
                  />
                </div>
                <div>
                  <Label htmlFor="presidente_cargo">Cargo</Label>
                  <Select 
                    value={formData.presidente_cargo || ''} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, presidente_cargo: v }))}
                  >
                    <SelectTrigger id="presidente_cargo" data-testid="select-presidente-cargo">
                      <SelectValue placeholder="Selecione o cargo" />
                    </SelectTrigger>
                    <SelectContent>
                      {CARGOS_CONTACTO.map(cargo => (
                        <SelectItem key={cargo} value={cargo}>{cargo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="presidente_telefone">Telefone</Label>
                  <Input
                    id="presidente_telefone"
                    value={formData.presidente_telefone || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, presidente_telefone: e.target.value }))}
                    placeholder="912 345 678"
                    data-testid="input-presidente-telefone"
                  />
                </div>
                <div>
                  <Label htmlFor="presidente_email">Email</Label>
                  <Input
                    id="presidente_email"
                    type="email"
                    value={formData.presidente_email || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, presidente_email: e.target.value }))}
                    placeholder="presidente@instituicao.pt"
                    data-testid="input-presidente-email"
                  />
                </div>
              </div>
            </div>

            {/* Pessoa de Contacto Institucional */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Pessoa de Contacto Institucional</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="contacto_nome">Nome</Label>
                  <Input
                    id="contacto_nome"
                    value={formData.contacto_nome || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, contacto_nome: e.target.value, pessoa_contacto: e.target.value }))}
                    placeholder="Nome"
                    data-testid="input-contacto"
                  />
                </div>
                <div>
                  <Label htmlFor="contacto_cargo">Cargo</Label>
                  <Select 
                    value={formData.contacto_cargo || ''} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, contacto_cargo: v }))}
                  >
                    <SelectTrigger id="contacto_cargo" data-testid="select-contacto-cargo">
                      <SelectValue placeholder="Selecione o cargo" />
                    </SelectTrigger>
                    <SelectContent>
                      {CARGOS_CONTACTO.map(cargo => (
                        <SelectItem key={cargo} value={cargo}>{cargo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="contacto_telefone">Telefone</Label>
                  <Input
                    id="contacto_telefone"
                    value={formData.contacto_telefone || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, contacto_telefone: e.target.value }))}
                    placeholder="912 345 678"
                    data-testid="input-contacto-telefone"
                  />
                </div>
                <div>
                  <Label htmlFor="contacto_email">Email</Label>
                  <Input
                    id="contacto_email"
                    type="email"
                    value={formData.contacto_email || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, contacto_email: e.target.value }))}
                    placeholder="contacto@instituicao.pt"
                    data-testid="input-contacto-email"
                  />
                </div>
              </div>
            </div>

            {/* Contactos Adicionais - Ver+ */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-slate-700">Contactos Adicionais</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addContactoAdicional}
                  className="text-blue-600 hover:text-blue-700"
                  data-testid="btn-add-contacto"
                >
                  <Plus className="w-4 h-4 mr-1" /> Ver+
                </Button>
              </div>
              
              {formData.contactos_adicionais?.length > 0 && (
                <div className="space-y-4">
                  {formData.contactos_adicionais.map((contacto, idx) => (
                    <div key={contacto.id || idx} className="p-3 bg-slate-50 rounded-lg border relative">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeContactoAdicional(idx)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700 h-6 w-6 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Nome</Label>
                          <Input
                            value={contacto.nome}
                            onChange={(e) => updateContactoAdicional(idx, 'nome', e.target.value)}
                            placeholder="Nome completo"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Cargo</Label>
                          <Select 
                            value={contacto.cargo} 
                            onValueChange={(v) => updateContactoAdicional(idx, 'cargo', v)}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Cargo" />
                            </SelectTrigger>
                            <SelectContent>
                              {CARGOS_CONTACTO.map(cargo => (
                                <SelectItem key={cargo} value={cargo}>{cargo}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Email</Label>
                          <Input
                            type="email"
                            value={contacto.email}
                            onChange={(e) => updateContactoAdicional(idx, 'email', e.target.value)}
                            placeholder="email@exemplo.pt"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Telefone</Label>
                          <Input
                            value={contacto.telefone}
                            onChange={(e) => updateContactoAdicional(idx, 'telefone', e.target.value)}
                            placeholder="912345678"
                            className="h-8"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {formData.contactos_adicionais?.length === 0 && (
                <p className="text-xs text-slate-400 italic">Sem contactos adicionais. Clique em Ver+ para adicionar.</p>
              )}
            </div>

            {/* Contactos da Instituição */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Contactos da Instituição</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="telefone">Telefone Geral</Label>
                  <Input
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => setFormData(prev => ({ ...prev, telefone: e.target.value }))}
                    placeholder="21 000 0000"
                    data-testid="input-telefone"
                  />
                </div>
                <div>
                  <Label htmlFor="email_principal">Email Geral</Label>
                  <Input
                    id="email_principal"
                    type="email"
                    value={formData.email_principal}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_principal: e.target.value }))}
                    placeholder="geral@instituicao.pt"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={formData.website || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="www.instituicao.pt"
                    data-testid="input-website"
                  />
                </div>
              </div>
            </div>

            {/* Recursos Humanos */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Recursos Humanos</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="num_tecnicos">Nº Técnicos</Label>
                  <Input
                    id="num_tecnicos"
                    type="number"
                    min="0"
                    value={formData.num_tecnicos}
                    onChange={(e) => setFormData(prev => ({ ...prev, num_tecnicos: parseInt(e.target.value) || 0 }))}
                    data-testid="input-tecnicos"
                  />
                </div>
                <div>
                  <Label htmlFor="num_outros_colaboradores">Outros Colaboradores Remunerados</Label>
                  <Input
                    id="num_outros_colaboradores"
                    type="number"
                    min="0"
                    value={formData.num_outros_colaboradores || 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, num_outros_colaboradores: parseInt(e.target.value) || 0 }))}
                    data-testid="input-outros-colaboradores"
                  />
                </div>
                <div>
                  <Label htmlFor="num_voluntarios">Nº Voluntários</Label>
                  <Input
                    id="num_voluntarios"
                    type="number"
                    min="0"
                    value={formData.num_voluntarios}
                    onChange={(e) => setFormData(prev => ({ ...prev, num_voluntarios: parseInt(e.target.value) || 0 }))}
                    data-testid="input-voluntarios"
                  />
                </div>
              </div>
            </div>

            {/* Banco Alimentar */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Banco Alimentar</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="codigo_banco_alimentar">Código BA</Label>
                  <Input
                    id="codigo_banco_alimentar"
                    value={formData.codigo_banco_alimentar || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, codigo_banco_alimentar: e.target.value }))}
                    placeholder="Código Banco Alimentar"
                    data-testid="input-codigo-ba"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="apoio_banco_alimentar"
                    checked={formData.apoio_banco_alimentar || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, apoio_banco_alimentar: e.target.checked }))}
                    className="w-4 h-4 text-[#00AA66] rounded border-slate-300 focus:ring-[#00AA66]"
                    data-testid="checkbox-apoio-ba"
                  />
                  <Label htmlFor="apoio_banco_alimentar" className="text-sm cursor-pointer">
                    Recebe apoio do Banco Alimentar
                  </Label>
                </div>
              </div>
            </div>

            {/* Origem */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Origem / Referência</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="origem">Origem da Instituição</Label>
                  <Select
                    value={formData.origem || ''}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, origem: value }))}
                  >
                    <SelectTrigger data-testid="select-origem">
                      <SelectValue placeholder="Selecione a origem" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Banco Alimentar">Banco Alimentar</SelectItem>
                      <SelectItem value="Dar e Receber">Dar e Receber</SelectItem>
                      <SelectItem value="Candidatura Portal">Candidatura Portal</SelectItem>
                      <SelectItem value="Indicação Parceiro">Indicação Parceiro</SelectItem>
                      <SelectItem value="Contacto Direto">Contacto Direto</SelectItem>
                      <SelectItem value="Evento">Evento</SelectItem>
                      <SelectItem value="Website">Website</SelectItem>
                      <SelectItem value="Rede Social">Rede Social</SelectItem>
                      <SelectItem value="Migração Sendys">Migração Sendys</SelectItem>
                      <SelectItem value="Outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="referenciado_por">Referenciado Por</Label>
                  <Input
                    id="referenciado_por"
                    value={formData.referenciado_por || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, referenciado_por: e.target.value }))}
                    placeholder="Nome ou entidade que referenciou"
                    data-testid="input-referenciado-por"
                  />
                </div>
              </div>
            </div>

            {/* Prioridade */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Classificação / Prioridade</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prioridade de Atendimento</Label>
                  <Select
                    value={formData.prioridade_atendimento || ''}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, prioridade_atendimento: value }))}
                  >
                    <SelectTrigger data-testid="select-prioridade">
                      <SelectValue placeholder="Selecione a prioridade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Crítica">Crítica</SelectItem>
                      <SelectItem value="Muito Alta">Muito Alta</SelectItem>
                      <SelectItem value="Alta">Alta</SelectItem>
                      <SelectItem value="Média-Alta">Média-Alta</SelectItem>
                      <SelectItem value="Média">Média</SelectItem>
                      <SelectItem value="Média-Baixa">Média-Baixa</SelectItem>
                      <SelectItem value="Baixa">Baixa</SelectItem>
                      <SelectItem value="Muito Baixa">Muito Baixa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="observacoes_prioridade">Observações Prioridade</Label>
                  <Input
                    id="observacoes_prioridade"
                    value={formData.observacoes_prioridade || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, observacoes_prioridade: e.target.value }))}
                    placeholder="Justificação da prioridade"
                    data-testid="input-obs-prioridade"
                  />
                </div>
              </div>
            </div>

            {/* Redes Sociais */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Redes Sociais</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="facebook">Facebook</Label>
                  <Input
                    id="facebook"
                    value={formData.redes_sociais?.facebook || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      redes_sociais: { ...prev.redes_sociais, facebook: e.target.value }
                    }))}
                    placeholder="https://facebook.com/..."
                    data-testid="input-facebook"
                  />
                </div>
                <div>
                  <Label htmlFor="instagram">Instagram</Label>
                  <Input
                    id="instagram"
                    value={formData.redes_sociais?.instagram || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      redes_sociais: { ...prev.redes_sociais, instagram: e.target.value }
                    }))}
                    placeholder="https://instagram.com/..."
                    data-testid="input-instagram"
                  />
                </div>
                <div>
                  <Label htmlFor="linkedin">LinkedIn</Label>
                  <Input
                    id="linkedin"
                    value={formData.redes_sociais?.linkedin || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      redes_sociais: { ...prev.redes_sociais, linkedin: e.target.value }
                    }))}
                    placeholder="https://linkedin.com/company/..."
                    data-testid="input-linkedin"
                  />
                </div>
                <div>
                  <Label htmlFor="website">Website Oficial</Label>
                  <Input
                    id="website"
                    value={formData.website || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://..."
                    data-testid="input-website"
                  />
                </div>
              </div>
            </div>

            {/* Respostas Sociais */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Respostas Sociais</Label>
                <Button type="button" variant="outline" size="sm" onClick={addValencia}>
                  <Plus className="w-3 h-3 mr-1" />
                  Adicionar
                </Button>
              </div>
              
              {/* Column Headers */}
              {formData.valencias.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg text-sm font-medium text-slate-600">
                  <div className="flex-1">Resposta Social</div>
                  <div className="w-20 text-center">Utentes</div>
                  <div className="w-20 text-center">Capac.</div>
                  <div className="w-24 text-center">Demografia</div>
                  <div className="w-8"></div>
                </div>
              )}
              
              <div className="space-y-3">
                {formData.valencias.map((val, index) => {
                  const valenciaInfo = valencias.find(v => v.id === val.valencia_id);
                  const categoria = valenciaInfo?.categoria || '';
                  const hasFaixasConfig = valenciaInfo?.faixas_etarias_permitidas?.length > 0;
                  const totalDemo = (val.utentes_masculino || 0) + (val.utentes_feminino || 0);
                  const hasOverCapacity = val.numero_utentes > val.capacidade_maxima && val.capacidade_maxima > 0;
                  const needsDemo = val.numero_utentes > 0 && totalDemo === 0;
                  const valKey = val.id || val.valencia_id || `valencia-${index}`;
                  
                  return (
                    <div key={valKey} className={`flex items-center gap-2 p-3 rounded-lg ${needsDemo ? 'bg-amber-50 border border-amber-200' : hasOverCapacity ? 'bg-orange-50 border border-orange-200' : 'bg-slate-50'}`}>
                      <div className="flex-1">
                        <ValenciaCombobox
                          value={val.valencia_id}
                          onChange={(v) => updateValencia(index, 'valencia_id', v)}
                          valencias={valencias}
                        />
                        {categoria && (
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{categoria}</Badge>
                            {hasFaixasConfig && (
                              <span className="text-xs text-blue-600">
                                {valenciaInfo.faixas_etarias_permitidas.length} faixas configuradas
                              </span>
                            )}
                          </div>
                        )}
                        {needsDemo && (
                          <p className="text-xs text-amber-600 mt-1">⚠️ Preencha dados demográficos</p>
                        )}
                        {hasOverCapacity && (
                          <p className="text-xs text-orange-600 mt-1">Sobre-utilização ({val.numero_utentes}/{val.capacidade_maxima})</p>
                        )}
                      </div>
                      <Input
                        type="number"
                        min="0"
                        placeholder="Utentes"
                        value={val.numero_utentes}
                        onChange={(e) => updateValencia(index, 'numero_utentes', parseInt(e.target.value) || 0)}
                        className={`w-20 ${hasOverCapacity ? 'border-orange-400' : ''}`}
                        data-testid={`input-utentes-${index}`}
                      />
                      <Input
                        type="number"
                        min="0"
                        placeholder="Capacidade"
                        value={val.capacidade_maxima}
                        onChange={(e) => updateValencia(index, 'capacidade_maxima', parseInt(e.target.value) || 0)}
                        className="w-20"
                        data-testid={`input-capacidade-${index}`}
                      />
                      <div className="w-24 flex flex-col items-center">
                        {val.valencia_id ? (
                          <>
                            <Button 
                              type="button" 
                              variant={needsDemo ? "default" : "outline"}
                              size="sm"
                              onClick={() => openDemoModal(index)}
                              className={`text-xs ${needsDemo ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                              data-testid={`btn-demo-${index}`}
                            >
                              <Users className="w-3 h-3 mr-1" />
                              {totalDemo > 0 ? `${totalDemo}` : 'Definir'}
                            </Button>
                            {totalDemo > 0 && (
                              <span className="text-xs text-slate-500 mt-1">
                                {val.utentes_masculino || 0}♂ / {val.utentes_feminino || 0}♀
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </div>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeValencia(index)}
                        className="text-red-600"
                        data-testid={`btn-remove-valencia-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
                {formData.valencias.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Nenhuma resposta social adicionada
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-[#0066CC] hover:bg-[#0052a3]"
                disabled={formLoading}
                data-testid="submit-btn"
              >
                {formLoading ? 'A guardar...' : modalMode === 'create' ? 'Criar Instituição' : 'Guardar Alterações'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Demografa de Valência numa Morada Adicional */}
      {moradaDemoModalOpen && (() => {
        const valencia = moradaForm.valencias[moradaDemoValenciaIdx];
        const totais = calcularTotaisMoradaDemoFaixas();
        return (
          <Dialog open={moradaDemoModalOpen} onOpenChange={setMoradaDemoModalOpen}>
            <DialogContent className="max-w-xl" data-testid="morada-demo-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#0066CC]" />
                  Utentes — {valencia?.valencia_nome}
                </DialogTitle>
                <DialogDescription>
                  Preencha os utentes por faixa etária e género para esta resposta social na morada.
                </DialogDescription>
              </DialogHeader>

              {moradaDemoFaixas.length === 0 ? (
                <div className="py-6 text-center text-slate-400 text-sm">
                  Esta resposta social não tem faixas etárias configuradas.<br />
                  Configure em <span className="font-medium">Configurações → Respostas Sociais</span>.
                </div>
              ) : (
                <div className="space-y-3 py-2">
                  <div className="grid grid-cols-3 gap-3 text-xs font-semibold text-slate-500 border-b pb-2">
                    <div>Faixa Etária</div>
                    <div className="text-center text-blue-700">Masculino</div>
                    <div className="text-center text-emerald-700">Feminino</div>
                  </div>
                  {moradaDemoFaixas.map(faixa => (
                    <div key={faixa.id || faixa.codigo} className="grid grid-cols-3 gap-3 items-center">
                      <div className="text-sm">{faixa.nome}</div>
                      <Input
                        type="number" min={0}
                        value={moradaDemoFaixasForm[`${faixa.codigo}_masculino`] || 0}
                        onChange={e => setMoradaDemoFaixasForm(prev => ({
                          ...prev, [`${faixa.codigo}_masculino`]: parseInt(e.target.value) || 0
                        }))}
                        className="text-center h-8"
                        data-testid={`morada-demo-${faixa.codigo}-m`}
                      />
                      <Input
                        type="number" min={0}
                        value={moradaDemoFaixasForm[`${faixa.codigo}_feminino`] || 0}
                        onChange={e => setMoradaDemoFaixasForm(prev => ({
                          ...prev, [`${faixa.codigo}_feminino`]: parseInt(e.target.value) || 0
                        }))}
                        className="text-center h-8"
                        data-testid={`morada-demo-${faixa.codigo}-f`}
                      />
                    </div>
                  ))}
                  <div className="grid grid-cols-3 gap-3 border-t pt-2 mt-2 text-sm font-semibold">
                    <div>Total</div>
                    <div className="text-center text-blue-700">{totais.masculino}</div>
                    <div className="text-center text-emerald-700">{totais.feminino}</div>
                  </div>
                  <div className="text-center text-sm font-semibold text-slate-700">
                    Total geral: {totais.total} utentes
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setMoradaDemoModalOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={saveMoradaDemoData}
                  className="bg-[#0066CC] hover:bg-[#0052a3]"
                  disabled={moradaDemoFaixas.length === 0}
                  data-testid="morada-demo-save-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Guardar Utentes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Dialog de Edição de Morada Adicional */}
      <Dialog open={moradaDialogOpen} onOpenChange={setMoradaDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="morada-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#0066CC]" />
              {moradaEditIdx !== null ? 'Editar Morada' : 'Nova Morada'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados da morada. O código postal preenche automaticamente a localidade e concelho.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Designação (ex: Sede, Polo Norte, Armazém)</Label>
              <Input
                value={moradaForm.designacao}
                onChange={e => setMoradaForm(f => ({ ...f, designacao: e.target.value }))}
                placeholder="Designação desta morada"
                className="h-8 text-sm"
                data-testid="morada-designacao"
              />
            </div>
            <div>
              <Label className="text-xs">Morada *</Label>
              <Input
                value={moradaForm.morada}
                onChange={e => setMoradaForm(f => ({ ...f, morada: e.target.value }))}
                placeholder="Rua, número..."
                className="h-8 text-sm"
                data-testid="morada-rua"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Código Postal</Label>
                <div className="relative">
                  <Input
                    value={moradaForm.codigo_postal}
                    onChange={e => setMoradaForm(f => ({ ...f, codigo_postal: e.target.value }))}
                    onBlur={e => handleMoradaCPLookup(e.target.value)}
                    placeholder="0000-000"
                    className="h-8 text-sm pr-7"
                    data-testid="morada-cp"
                  />
                  {moradaCpLoading && <Loader2 className="absolute right-2 top-1.5 w-3.5 h-3.5 animate-spin text-slate-400" />}
                </div>
              </div>
              <div>
                <Label className="text-xs">Localidade</Label>
                <Input
                  value={moradaForm.localidade}
                  onChange={e => setMoradaForm(f => ({ ...f, localidade: e.target.value }))}
                  placeholder="Localidade"
                  className="h-8 text-sm"
                  data-testid="morada-localidade"
                />
              </div>
              <div>
                <Label className="text-xs">Concelho</Label>
                <Input
                  value={moradaForm.concelho}
                  onChange={e => setMoradaForm(f => ({ ...f, concelho: e.target.value }))}
                  placeholder="Concelho"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Distrito</Label>
                <Input
                  value={moradaForm.distrito}
                  onChange={e => setMoradaForm(f => ({ ...f, distrito: e.target.value }))}
                  placeholder="Distrito"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Telefone</Label>
                <Input
                  value={moradaForm.telefone}
                  onChange={e => setMoradaForm(f => ({ ...f, telefone: e.target.value }))}
                  placeholder="Telefone"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  value={moradaForm.email}
                  onChange={e => setMoradaForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="email@morada.pt"
                  className="h-8 text-sm"
                />
              </div>
            </div>

            {/* Respostas Sociais desta morada */}
            <div>
              <Label className="text-xs font-semibold">Respostas Sociais associadas a esta morada</Label>
              <div className="mt-1">
                <Select onValueChange={(vid) => {
                  const v = valencias.find(x => x.id === vid);
                  if (!v || moradaForm.valencias.find(x => x.valencia_id === vid)) return;
                  setMoradaForm(f => ({ ...f, valencias: [...f.valencias, { valencia_id: vid, valencia_nome: v.nome, numero_utentes: 0 }] }));
                }}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Adicionar resposta social..." />
                  </SelectTrigger>
                  <SelectContent>
                    {valencias.filter(v => !moradaForm.valencias.find(x => x.valencia_id === v.id)).map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {moradaForm.valencias.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {moradaForm.valencias.map((v, idx) => (
                    <div key={v.valencia_id} className="p-2 bg-slate-50 rounded border">
                      <div className="flex items-center gap-2">
                        <span className="flex-1 truncate text-xs font-medium">{v.valencia_nome}</span>
                        {v.numero_utentes > 0 && (
                          <span className="text-xs text-blue-600 font-medium">
                            {v.utentes_masculino ?? 0}M + {v.utentes_feminino ?? 0}F = {v.numero_utentes} utentes
                          </span>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 text-xs px-2 text-blue-700 border-blue-200 hover:bg-blue-50"
                          onClick={() => openMoradaDemoModal(idx)}
                          disabled={loadingMoradaDemoFaixas}
                          data-testid={`morada-demo-btn-${idx}`}
                        >
                          {loadingMoradaDemoFaixas && moradaDemoValenciaIdx === idx
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <><Users className="w-3 h-3 mr-1" />Utentes</>
                          }
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-red-400 hover:text-red-600"
                          onClick={() => setMoradaForm(f => ({ ...f, valencias: f.valencias.filter(x => x.valencia_id !== v.valencia_id) }))}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs">Observações</Label>
              <Textarea
                value={moradaForm.observacoes}
                onChange={e => setMoradaForm(f => ({ ...f, observacoes: e.target.value }))}
                rows={2}
                className="text-sm"
                placeholder="Notas adicionais sobre esta morada..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setMoradaDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={saveMoradaDialog} className="bg-[#0066CC] hover:bg-[#0052a3]" data-testid="morada-save-btn">
              <CheckCircle className="w-4 h-4 mr-2" />
              {moradaEditIdx !== null ? 'Guardar Morada' : 'Adicionar Morada'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="view-modal">
          {selectedInstituicao && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-[#0066CC]" />
                  {selectedInstituicao.nome}
                </DialogTitle>
                <DialogDescription>
                  Código: {selectedInstituicao.codigo_ea}
                </DialogDescription>
              </DialogHeader>
              
              {/* Tabs para organizar informação */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-4">
                  <TabsTrigger value="dados" className="flex items-center gap-1" data-testid="tab-dados">
                    <Building2 className="w-4 h-4" /> Dados
                  </TabsTrigger>
                  <TabsTrigger value="documentos" className="flex items-center gap-1" data-testid="tab-documentos">
                    <FileText className="w-4 h-4" /> Documentos
                  </TabsTrigger>
                  <TabsTrigger value="historico" className="flex items-center gap-1" data-testid="tab-historico">
                    <History className="w-4 h-4" /> Histórico
                  </TabsTrigger>
                  <TabsTrigger value="contactos" className="flex items-center gap-1" data-testid="tab-contactos">
                    <MessageSquare className="w-4 h-4" /> Contactos
                  </TabsTrigger>
                </TabsList>

                {/* TAB: Dados da Instituição */}
                <TabsContent value="dados" className="space-y-6">
                {/* Dados Básicos */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Tipo de Entidade</p>
                    <p className="font-medium">{selectedInstituicao.tipo_entidade}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Status</p>
                    {(() => {
                      const estadoConfig = getEstadoConfig(selectedInstituicao.estado);
                      return (
                        <Badge className={estadoConfig.color}>
                          {estadoConfig.label}
                        </Badge>
                      );
                    })()}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">NIF</p>
                    <p className="font-medium font-mono">{selectedInstituicao.nif || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Situação Financeira</p>
                    <Badge variant="outline">{selectedInstituicao.situacao_financeira || '-'}</Badge>
                  </div>
                </div>

                {/* Localização */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Localização
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Distrito / Concelho</p>
                      <p className="font-medium">{selectedInstituicao.distrito_nome} {selectedInstituicao.concelho_nome ? `/ ${selectedInstituicao.concelho_nome}` : ''}</p>
                    </div>
                    {selectedInstituicao.morada && (
                      <div className="col-span-2">
                        <p className="text-xs text-slate-500">Morada</p>
                        <p className="font-medium">{selectedInstituicao.morada}</p>
                      </div>
                    )}
                    {selectedInstituicao.codigo_postal && (
                      <div>
                        <p className="text-xs text-slate-500">Código Postal</p>
                        <p className="font-medium font-mono">{selectedInstituicao.codigo_postal}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contactos */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-3 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contactos
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedInstituicao.telefone && (
                      <div>
                        <p className="text-xs text-slate-500">Telefone</p>
                        <p className="font-medium">{selectedInstituicao.telefone}</p>
                      </div>
                    )}
                    {selectedInstituicao.email_principal && (
                      <div>
                        <p className="text-xs text-slate-500">Email</p>
                        <p className="font-medium flex items-center gap-1">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {selectedInstituicao.email_principal}
                        </p>
                      </div>
                    )}
                    {selectedInstituicao.pessoa_contacto && (
                      <div>
                        <p className="text-xs text-slate-500">Pessoa de Contacto</p>
                        <p className="font-medium">{selectedInstituicao.pessoa_contacto}</p>
                      </div>
                    )}
                    {selectedInstituicao.representante_legal && (
                      <div>
                        <p className="text-xs text-slate-500">Representante Legal</p>
                        <p className="font-medium">{selectedInstituicao.representante_legal}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Presidente/Representante Legal (novos campos do formulário) */}
                {(selectedInstituicao.presidente_nome || selectedInstituicao.presidente_email) && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-sm font-semibold text-green-700 dark:text-green-300 mb-3">Presidente / Representante Legal</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Nome</p>
                        <p className="font-medium">{selectedInstituicao.presidente_nome || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Cargo</p>
                        <p className="font-medium">{selectedInstituicao.presidente_cargo || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Telefone</p>
                        <p className="font-medium">{selectedInstituicao.presidente_telefone || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Email</p>
                        <p className="font-medium">{selectedInstituicao.presidente_email || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sobre a Instituição */}
                {(selectedInstituicao.missao || selectedInstituicao.populacao_apoiada || selectedInstituicao.area_geografica) && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <p className="text-sm font-semibold text-amber-700 dark:text-amber-300 mb-3">Sobre a Instituição</p>
                    <div className="space-y-3">
                      {selectedInstituicao.missao && (
                        <div>
                          <p className="text-xs text-slate-500">Missão</p>
                          <p className="font-medium text-sm">{selectedInstituicao.missao}</p>
                        </div>
                      )}
                      {selectedInstituicao.populacao_apoiada && (
                        <div>
                          <p className="text-xs text-slate-500">População Apoiada</p>
                          <p className="font-medium text-sm">{selectedInstituicao.populacao_apoiada}</p>
                        </div>
                      )}
                      {selectedInstituicao.area_geografica && (
                        <div>
                          <p className="text-xs text-slate-500">Área Geográfica</p>
                          <p className="font-medium text-sm">{selectedInstituicao.area_geografica}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Estatísticas de RH e Utentes */}
                <div className="grid grid-cols-4 gap-4">
                  <Card className="card-metric">
                    <CardContent className="pt-4">
                      <p className="text-2xl font-bold text-[#0066CC]">{selectedInstituicao.total_utentes || selectedInstituicao.total_utentes_apoiados || 0}</p>
                      <p className="text-xs text-slate-500">Utentes Apoiados</p>
                    </CardContent>
                  </Card>
                  <Card className="card-metric">
                    <CardContent className="pt-4">
                      <p className="text-2xl font-bold text-[#00AA66]">{selectedInstituicao.num_tecnicos || selectedInstituicao.num_colaboradores_remunerados || 0}</p>
                      <p className="text-xs text-slate-500">Colaboradores</p>
                    </CardContent>
                  </Card>
                  <Card className="card-metric">
                    <CardContent className="pt-4">
                      <p className="text-2xl font-bold text-purple-600">{selectedInstituicao.num_voluntarios || selectedInstituicao.num_voluntarios_regulares || 0}</p>
                      <p className="text-xs text-slate-500">Voluntários</p>
                    </CardContent>
                  </Card>
                  <Card className="card-metric">
                    <CardContent className="pt-4">
                      <p className="text-2xl font-bold text-amber-600">{selectedInstituicao.valencias?.length || 0}</p>
                      <p className="text-xs text-slate-500">Respostas Sociais</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Atividade Social - Utentes por Resposta Social */}
                {selectedInstituicao.utentes_por_resposta_social && Object.keys(selectedInstituicao.utentes_por_resposta_social).length > 0 && (
                  <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                    <p className="text-sm font-semibold text-cyan-700 dark:text-cyan-300 mb-3">Utentes por Resposta Social</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(selectedInstituicao.utentes_por_resposta_social).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-sm bg-white dark:bg-slate-700 p-2 rounded">
                          <span className="text-slate-600 dark:text-slate-300">{key}</span>
                          <span className="font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Distribuição de Bens */}
                {selectedInstituicao.distribuicao_bens && Object.keys(selectedInstituicao.distribuicao_bens).length > 0 && (
                  <div className="p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                    <p className="text-sm font-semibold text-pink-700 dark:text-pink-300 mb-3">Distribuição de Bens</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(selectedInstituicao.distribuicao_bens).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-sm bg-white dark:bg-slate-700 p-2 rounded">
                          <span className="text-slate-600 dark:text-slate-300">{key}</span>
                          <span className="font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Outras Respostas Sociais */}
                {selectedInstituicao.outras_respostas_sociais && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Outras Respostas Sociais</p>
                    <p className="font-medium text-sm p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">{selectedInstituicao.outras_respostas_sociais}</p>
                  </div>
                )}

                {/* Áreas de Interesse */}
                {selectedInstituicao.areas_interesse?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Áreas de Interesse</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedInstituicao.areas_interesse.map((area, i) => (
                        <Badge key={i} variant="outline" className="bg-slate-100 dark:bg-slate-700">
                          {area}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Respostas Sociais */}
                {selectedInstituicao.valencias?.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Respostas Sociais ({selectedInstituicao.valencias.length})</p>
                    <div className="space-y-2">
                      {selectedInstituicao.valencias.map((v, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{v.valencia_nome}</p>
                            <p className="text-xs text-slate-500">{v.valencia_codigo} - {v.categoria}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-[#0066CC]">{v.numero_utentes}</p>
                            <p className="text-xs text-slate-500">de {v.capacidade_maxima} cap.</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Utilizadores do Portal */}
                <UtilizadoresPortalSection instituicaoId={selectedInstituicao?.id} />
                </TabsContent>

                {/* TAB: Documentos em Aberto (Conta Corrente) */}
                <TabsContent value="documentos" className="space-y-4">
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-300 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Documentos Pendentes (Conta Corrente)
                    </h4>
                    
                    {/* Adicionar novo documento */}
                    <div className="mb-4 p-3 bg-white dark:bg-slate-800 rounded-lg border">
                      <p className="text-xs text-slate-500 mb-2">Adicionar Documento Pendente:</p>
                      <div className="grid grid-cols-4 gap-2">
                        <Select
                          value={novoDocumento.tipo}
                          onValueChange={(v) => setNovoDocumento(prev => ({ ...prev, tipo: v }))}
                        >
                          <SelectTrigger className="h-8" data-testid="select-doc-tipo">
                            <SelectValue placeholder="Tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Estatutos">Estatutos</SelectItem>
                            <SelectItem value="Relatório Contas">Relatório Contas</SelectItem>
                            <SelectItem value="Plano Atividades">Plano Atividades</SelectItem>
                            <SelectItem value="Certidão Permanente">Certidão Permanente</SelectItem>
                            <SelectItem value="Declaração IRS">Declaração IRS</SelectItem>
                            <SelectItem value="Comprovativo NIF">Comprovativo NIF</SelectItem>
                            <SelectItem value="Outro">Outro</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          className="h-8 col-span-2"
                          placeholder="Descrição..."
                          value={novoDocumento.descricao}
                          onChange={(e) => setNovoDocumento(prev => ({ ...prev, descricao: e.target.value }))}
                          data-testid="input-doc-descricao"
                        />
                        <Button 
                          size="sm" 
                          onClick={addDocumentoPendente}
                          className="h-8 bg-amber-600 hover:bg-amber-700"
                          data-testid="btn-add-documento"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Lista de documentos pendentes */}
                    {documentosPendentes.length > 0 ? (
                      <div className="space-y-2">
                        {documentosPendentes.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border">
                            <div>
                              <p className="font-medium text-sm">{doc.tipo}</p>
                              <p className="text-xs text-slate-500">{doc.descricao}</p>
                              {doc.data_limite && (
                                <p className="text-xs text-red-500 mt-1">
                                  <Calendar className="w-3 h-3 inline mr-1" />
                                  Prazo: {new Date(doc.data_limite).toLocaleDateString('pt-PT')}
                                </p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => marcarDocumentoEntregue(doc.id)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              data-testid={`btn-doc-entregue-${doc.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Entregue
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-4">
                        Sem documentos pendentes.
                      </p>
                    )}
                  </div>

                  {/* Histórico de Documentos Entregues */}
                  {documentosHistorico.length > 0 && (
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <h4 className="text-sm font-semibold text-green-700 dark:text-green-300 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Documentos Entregues
                      </h4>
                      <div className="space-y-2">
                        {documentosHistorico.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded border text-sm">
                            <div>
                              <span className="font-medium">{doc.tipo}</span>
                              <span className="text-slate-400 mx-2">-</span>
                              <span className="text-slate-500">{doc.descricao}</span>
                            </div>
                            <span className="text-xs text-green-600">
                              {doc.data_entrega && new Date(doc.data_entrega).toLocaleDateString('pt-PT')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* TAB: Histórico de Alterações */}
                <TabsContent value="historico" className="space-y-4">
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-3 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Histórico de Alterações de Dados
                    </h4>
                    
                    {loadingHistorico ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                        <span className="ml-2 text-slate-500">A carregar histórico...</span>
                      </div>
                    ) : historico.length > 0 ? (
                      <div className="space-y-3 max-h-[400px] overflow-y-auto">
                        {historico.map((entry, idx) => (
                          <div key={entry.id || idx} className="p-3 bg-white dark:bg-slate-800 rounded-lg border-l-4 border-purple-400">
                            <div className="flex justify-between items-start mb-1">
                              <span className="text-xs font-medium text-purple-600">{entry.tipo_alteracao || 'Alteração'}</span>
                              <span className="text-xs text-slate-400">
                                {entry.data_alteracao && new Date(entry.data_alteracao).toLocaleDateString('pt-PT', { 
                                  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                                })}
                              </span>
                            </div>
                            <p className="text-sm text-slate-700 dark:text-slate-300">{entry.descricao}</p>
                            {entry.campo_alterado && (
                              <div className="mt-2 text-xs">
                                <span className="text-slate-500">Campo: </span>
                                <span className="font-medium">{entry.campo_alterado}</span>
                                {entry.valor_anterior && (
                                  <span className="text-red-500 line-through ml-2">{entry.valor_anterior}</span>
                                )}
                                {entry.valor_novo && (
                                  <span className="text-green-600 ml-2">{entry.valor_novo}</span>
                                )}
                              </div>
                            )}
                            {entry.utilizador && (
                              <p className="text-xs text-slate-400 mt-1">Por: {entry.utilizador}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-8">
                        Sem histórico de alterações registado.
                      </p>
                    )}
                  </div>
                </TabsContent>

                {/* TAB: Registo de Contactos Internos */}
                <TabsContent value="contactos" className="space-y-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Registo de Contactos com a Instituição
                    </h4>
                    
                    {/* Adicionar novo contacto */}
                    <div className="mb-4 p-3 bg-white dark:bg-slate-800 rounded-lg border">
                      <p className="text-xs text-slate-500 mb-2">Registar Novo Contacto:</p>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <Select
                          value={novoContacto.tipo}
                          onValueChange={(v) => setNovoContacto(prev => ({ ...prev, tipo: v }))}
                        >
                          <SelectTrigger className="h-8" data-testid="select-contacto-tipo">
                            <SelectValue placeholder="Tipo de Contacto" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Telefone">Telefone</SelectItem>
                            <SelectItem value="Email">Email</SelectItem>
                            <SelectItem value="Reunião Presencial">Reunião Presencial</SelectItem>
                            <SelectItem value="Reunião Online">Reunião Online</SelectItem>
                            <SelectItem value="Visita">Visita à Instituição</SelectItem>
                            <SelectItem value="Outro">Outro</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          className="h-8"
                          placeholder="Assunto"
                          value={novoContacto.assunto}
                          onChange={(e) => setNovoContacto(prev => ({ ...prev, assunto: e.target.value }))}
                          data-testid="input-contacto-assunto"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Textarea
                          className="flex-1 min-h-[60px]"
                          placeholder="Descrição / Notas do contacto..."
                          value={novoContacto.descricao}
                          onChange={(e) => setNovoContacto(prev => ({ ...prev, descricao: e.target.value }))}
                          data-testid="textarea-contacto-descricao"
                        />
                        <Button 
                          onClick={addContactoRegistado}
                          className="bg-blue-600 hover:bg-blue-700 self-end"
                          data-testid="btn-add-contacto"
                        >
                          <Plus className="w-4 h-4 mr-1" /> Registar
                        </Button>
                      </div>
                    </div>

                    {/* Lista de contactos registados */}
                    {loadingContactos ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                        <span className="ml-2 text-slate-500">A carregar contactos...</span>
                      </div>
                    ) : contactosRegistados.length > 0 ? (
                      <div className="space-y-3 max-h-[350px] overflow-y-auto">
                        {contactosRegistados.map((contacto) => (
                          <div key={contacto.id} className="p-3 bg-white dark:bg-slate-800 rounded-lg border-l-4 border-blue-400">
                            <div className="flex justify-between items-start">
                              <div>
                                <Badge variant="outline" className="text-xs mb-1">{contacto.tipo}</Badge>
                                <p className="font-medium text-sm">{contacto.assunto}</p>
                                {contacto.descricao && (
                                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{contacto.descricao}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-400">
                                  {contacto.data && new Date(contacto.data).toLocaleDateString('pt-PT')}
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => deleteContactoRegistado(contacto.id)}
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`btn-delete-contacto-${contacto.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            {contacto.registado_por && (
                              <p className="text-xs text-slate-400 mt-2">Registado por: {contacto.registado_por}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-8">
                        Sem contactos registados com esta instituição.
                      </p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Modal de Demografia Detalhada por Resposta Social */}
      <Dialog open={demoModalOpen} onOpenChange={setDemoModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#0066CC]" />
              Dados Demográficos por Faixa Etária
            </DialogTitle>
            <DialogDescription>
              {demoValenciaIndex !== null && formData.valencias[demoValenciaIndex] && (
                <>
                  {valencias.find(v => v.id === formData.valencias[demoValenciaIndex]?.valencia_id)?.nome}
                  {demoFaixas.length > 0 && (
                    <Badge className="ml-2 bg-blue-100 text-blue-700">{demoFaixas.length} faixas etárias</Badge>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {loadingDemoFaixas ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
                <span className="ml-2 text-slate-500">A carregar faixas etárias...</span>
              </div>
            ) : demoFaixas.length > 0 ? (
              <>
                {/* Tabela de Faixas Dinâmicas */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium">Faixa Etária</th>
                        <th className="px-4 py-2 text-center text-sm font-medium text-blue-600">♂ Masculino</th>
                        <th className="px-4 py-2 text-center text-sm font-medium text-pink-600">♀ Feminino</th>
                        <th className="px-4 py-2 text-center text-sm font-medium">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demoFaixas.map((faixa, i) => {
                        const m = demoFaixasForm[`${faixa.codigo}_masculino`] || 0;
                        const f = demoFaixasForm[`${faixa.codigo}_feminino`] || 0;
                        return (
                          <tr key={faixa.id || i} className="border-t hover:bg-slate-50">
                            <td className="px-4 py-2">
                              <div className="text-sm font-medium">{faixa.nome}</div>
                              <div className="text-xs text-slate-400">{faixa.idade_min}-{faixa.idade_max} anos</div>
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                type="number"
                                min="0"
                                className="w-20 mx-auto h-8 text-center"
                                value={m}
                                onChange={(e) => setDemoFaixasForm(prev => ({ 
                                  ...prev, 
                                  [`${faixa.codigo}_masculino`]: parseInt(e.target.value) || 0 
                                }))}
                                data-testid={`demo-${faixa.codigo}-m`}
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                type="number"
                                min="0"
                                className="w-20 mx-auto h-8 text-center"
                                value={f}
                                onChange={(e) => setDemoFaixasForm(prev => ({ 
                                  ...prev, 
                                  [`${faixa.codigo}_feminino`]: parseInt(e.target.value) || 0 
                                }))}
                                data-testid={`demo-${faixa.codigo}-f`}
                              />
                            </td>
                            <td className="px-4 py-2 text-center font-medium">{m + f}</td>
                          </tr>
                        );
                      })}
                      <tr className="border-t bg-slate-100 font-semibold">
                        <td className="px-4 py-2">TOTAL</td>
                        <td className="px-4 py-2 text-center text-blue-600">{calcularTotaisDemoFaixas().masculino}</td>
                        <td className="px-4 py-2 text-center text-pink-600">{calcularTotaisDemoFaixas().feminino}</td>
                        <td className="px-4 py-2 text-center font-bold">{calcularTotaisDemoFaixas().total}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="p-3 bg-blue-50 rounded-lg flex justify-between items-center">
                  <span className="text-blue-700">Total Geral:</span>
                  <span className="font-bold text-blue-900 text-lg">{calcularTotaisDemoFaixas().total} utentes</span>
                </div>
              </>
            ) : (
              /* Fallback - sem faixas configuradas */
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Esta resposta social não tem faixas etárias específicas configuradas.</p>
                <p className="text-sm mt-1">Configure as faixas em Configurações → Respostas Sociais.</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDemoModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={saveDemoData} 
              className="bg-[#0066CC] hover:bg-[#0055AA]"
              disabled={demoFaixas.length === 0}
              data-testid="save-demo-btn"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
